import React, { ReactElement } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import { persistStore } from 'redux-persist';
import ReactGA from 'react-ga4';
import { ReactSDKClient as OptimizelyClient } from '@optimizely/react-sdk';
import theme from './assets/theme';
import App from './App';
import './index.css';
import reportWebVitals from './reportWebVitals';
import './assets/i18n/config';
import { setupStore } from './store/storeSetup';
import { injectStore } from './api/apiConfig';
import { initSentry } from './config/sentry';
import { Heap, ReactHeap } from './config/heap';
import { getGAMeasurementId } from './config/googleAnalytics';
import { ReactCustomerIO } from './config/customerIo';
import { ReactOptimizely } from './config/optimizely';
import { AppOptimizelyProvider } from './config/AppOptimizelyProvider';
import { ReactChameleon } from './config/chameleon';
import { redirectTokenLoginOffLoginPage } from './common/utils/autoLoginRoute';

declare global {
  // eslint-disable-next-line vars-on-top, no-var
  var heap: Heap;
  // eslint-disable-next-line vars-on-top, no-var
  var optimizely: OptimizelyClient;
}

// Move token auto-logins off /login before Heap records the pageview (SN-1166)
redirectTokenLoginOffLoginPage();

// Initialize Heap tracking
ReactHeap.initialize();

ReactCustomerIO.initialize();

// Initialize Chameleon
ReactChameleon.initialize();

// Initialize Google Analytics tracking
ReactGA.initialize(
  getGAMeasurementId(),
);

initSentry();

// redux store
const store = setupStore();
const persistor = persistStore(store);

global.optimizely = ReactOptimizely.initialize(store);

// inject store in api config to enable retrieving of access token
injectStore(store);

// start up mock service worker in development env
// TODO: disable this once all api endpoints exist on backend
// alternatively could just disable the handlers instead
async function enableMocking() {
  if (process.env.NODE_ENV !== 'development' ||
  process.env.REACT_APP_ENABLE_API_MOCKING !== 'true') {
    return Promise.resolve();
  }

  const { worker } = await import('./api/mocks/browser');

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  return worker.start({
    // disable the logging of matched requests in a browser's console
    quiet: false,
    // perform as-is any request that is not listed in the request handlers
    onUnhandledRequest: 'bypass',
  });
}

const root = ReactDOM.createRoot(
  // eslint-disable-next-line no-undef
  document.getElementById('root') as HTMLElement,
);

const middlewares = Array.from<((element: ReactElement) => ReactElement)>([
  (children) => <React.StrictMode>{children}</React.StrictMode>,
  (children) => <Provider store={store}>{children}</Provider>,
  (children) => <AppOptimizelyProvider>{children}</AppOptimizelyProvider>,
  (children) => <PersistGate persistor={persistor}>{children}</PersistGate>,
  (children) => <BrowserRouter>{children}</BrowserRouter>,
  (children) => <ThemeProvider theme={theme}>{children}</ThemeProvider>,
  (children) => <><CssBaseline />{children}</>,
]).reverse()
  .filter((x) => x);

const app = middlewares.reduce((element, middleware) => middleware(element), <App />);

enableMocking().then(() => {
  root.render(app);
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
